import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Wifi, Plus, Server, RefreshCw, Trash2, TestTube, Building2, CheckCircle, XCircle, Clock, Edit2, Save, X, ChevronDown, ChevronUp, Globe, Monitor, Router } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../utils/api';
import DashboardLayout from '../components/DashboardLayout';

const UniFiControllers = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [controllers, setControllers] = useState([]);
  const [clients, setClients] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingController, setEditingController] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [syncingId, setSyncingId] = useState(null);
  const [expandedController, setExpandedController] = useState(null);
  const [devicesData, setDevicesData] = useState({});
  const [loadingDevices, setLoadingDevices] = useState(null);
  
  const [newController, setNewController] = useState({
    name: '',
    url: '',
    username: '',
    password: '',
    api_key: '',
    client_ids: [],
    auto_sync: false,
    sync_interval_hours: 24
  });

  // Detect if URL is UniFi Cloud
  const isCloudUrl = (url) => url?.toLowerCase().includes('unifi.ui.com');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [controllersRes, clientsRes] = await Promise.all([
        api.get('/unifi-controllers'),
        api.get('/clients')
      ]);
      setControllers(controllersRes.data.controllers || []);
      setClients(clientsRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddController = async () => {
    const isCloud = isCloudUrl(newController.url);
    
    if (!newController.name || !newController.url) {
      toast.error('Please fill in name and URL');
      return;
    }
    
    if (isCloud && !newController.api_key) {
      toast.error('API Key is required for UniFi Cloud');
      return;
    }
    
    if (!isCloud && (!newController.username || !newController.password)) {
      toast.error('Username and Password are required for local controllers');
      return;
    }

    try {
      const response = await api.post('/unifi-controllers', newController);
      setControllers([...controllers, response.data.controller]);
      setShowAddDialog(false);
      setNewController({
        name: '',
        url: '',
        username: '',
        password: '',
        api_key: '',
        client_ids: [],
        auto_sync: false,
        sync_interval_hours: 24
      });
      toast.success('Controller added successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add controller');
    }
  };

  const handleUpdateController = async (controllerId) => {
    try {
      await api.put(`/unifi-controllers/${controllerId}`, editingController);
      setControllers(controllers.map(c => 
        c.id === controllerId ? { ...c, ...editingController } : c
      ));
      setEditingController(null);
      toast.success('Controller updated');
    } catch (error) {
      toast.error('Failed to update controller');
    }
  };

  const handleDeleteController = async (controllerId) => {
    if (!confirm('Are you sure you want to delete this controller?')) return;

    try {
      await api.delete(`/unifi-controllers/${controllerId}`);
      setControllers(controllers.filter(c => c.id !== controllerId));
      toast.success('Controller removed');
    } catch (error) {
      toast.error('Failed to remove controller');
    }
  };

  const handleTestController = async (controllerId) => {
    setTestingId(controllerId);
    try {
      const response = await api.post(`/unifi-controllers/${controllerId}/test`);
      if (response.data.success) {
        toast.success(response.data.message);
        // Update device count
        setControllers(controllers.map(c => 
          c.id === controllerId ? { ...c, device_count: response.data.device_count } : c
        ));
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to test connection');
    } finally {
      setTestingId(null);
    }
  };

  const handleSyncController = async (controllerId) => {
    setSyncingId(controllerId);
    try {
      const response = await api.post(`/unifi-controllers/${controllerId}/sync`);
      toast.success(response.data.message);
      // Refresh controllers to get updated sync status
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync devices');
    } finally {
      setSyncingId(null);
    }
  };

  const handleToggleDevices = async (controllerId) => {
    if (expandedController === controllerId) {
      setExpandedController(null);
      return;
    }
    
    setExpandedController(controllerId);
    
    // Only fetch if we don't have data yet
    if (!devicesData[controllerId]) {
      setLoadingDevices(controllerId);
      try {
        const response = await api.get(`/unifi-controllers/${controllerId}/devices`);
        setDevicesData(prev => ({
          ...prev,
          [controllerId]: response.data
        }));
      } catch (error) {
        toast.error('Failed to fetch devices');
      } finally {
        setLoadingDevices(null);
      }
    }
  };

  const handleAssignClientToSite = async (controllerId, siteId, clientId) => {
    try {
      await api.post(`/unifi-controllers/${controllerId}/sites/${siteId}/assign-client`, {
        client_id: clientId || null
      });
      
      // Update local state
      setDevicesData(prev => {
        const controllerData = prev[controllerId];
        if (!controllerData) return prev;
        
        const updatedSites = controllerData.sites.map(site => 
          site.id === siteId ? { ...site, assigned_client_id: clientId } : site
        );
        
        return {
          ...prev,
          [controllerId]: { ...controllerData, sites: updatedSites }
        };
      });
      
      toast.success(clientId ? 'Client assigned to site' : 'Client assignment removed');
    } catch (error) {
      toast.error('Failed to assign client');
    }
  };

  const handleClientSelection = (clientId, isSelected, isEditing = false) => {
    if (isEditing && editingController) {
      const currentIds = editingController.client_ids || [];
      setEditingController({
        ...editingController,
        client_ids: isSelected 
          ? [...currentIds, clientId]
          : currentIds.filter(id => id !== clientId)
      });
    } else {
      const currentIds = newController.client_ids || [];
      setNewController({
        ...newController,
        client_ids: isSelected 
          ? [...currentIds, clientId]
          : currentIds.filter(id => id !== clientId)
      });
    }
  };

  const handleSyncSite = async (controllerId, siteId, siteName) => {
    setSyncingId(`${controllerId}-${siteId}`);
    try {
      const response = await api.post(`/unifi-controllers/${controllerId}/sync-site/${siteId}`);
      toast.success(response.data.message || `Synced devices from ${siteName}`);
      // Refresh devices data
      const devicesResponse = await api.get(`/unifi-controllers/${controllerId}/devices`);
      setDevicesData(prev => ({
        ...prev,
        [controllerId]: devicesResponse.data
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync site devices');
    } finally {
      setSyncingId(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/settings/integrations')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Wifi className="w-6 h-6 text-blue-600" />
                UniFi Site Manager
              </h1>
              <p className="text-gray-500 text-sm">Connect to UniFi Cloud to sync network devices, switches, APs, and clients to assets</p>
            </div>
          </div>
          <Button onClick={() => setShowAddDialog(true)} className="gap-2">
            <Plus className="w-4 h-4" />
            Add Site Manager
          </Button>
        </div>

        {/* Controllers List */}
        {controllers.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Server className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-700">No Site Manager Configured</h3>
              <p className="text-gray-500 mt-1">Connect your UniFi Site Manager to start syncing network devices to assets</p>
              <Button onClick={() => setShowAddDialog(true)} className="mt-4 gap-2">
                <Plus className="w-4 h-4" />
                Add Site Manager
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {controllers.map((controller) => (
              <Card key={controller.id} className="overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50 pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                        <Wifi className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{controller.name}</CardTitle>
                        <CardDescription className="font-mono text-xs">{controller.url}</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant="outline" 
                        className="gap-1 cursor-pointer hover:bg-blue-50 transition-colors"
                        onClick={() => handleToggleDevices(controller.id)}
                      >
                        <Server className="w-3 h-3" />
                        {controller.device_count || 0} sites/devices
                        {expandedController === controller.id ? (
                          <ChevronUp className="w-3 h-3 ml-1" />
                        ) : (
                          <ChevronDown className="w-3 h-3 ml-1" />
                        )}
                      </Badge>
                      {controller.auto_sync && (
                        <Badge className="bg-green-100 text-green-800">Auto-Sync</Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  {/* Expanded Devices/Sites View */}
                  {expandedController === controller.id && (
                    <div className="mb-4 p-4 bg-gray-50 rounded-lg border">
                      {loadingDevices === controller.id ? (
                        <div className="flex items-center justify-center py-4">
                          <RefreshCw className="w-5 h-5 animate-spin text-blue-600 mr-2" />
                          <span className="text-gray-600">Loading sites...</span>
                        </div>
                      ) : devicesData[controller.id] ? (
                        <div className="space-y-4">
                          {/* Sites (Dream Machines) */}
                          {devicesData[controller.id].sites?.length > 0 && (
                            <div>
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="font-medium text-gray-700 flex items-center gap-2">
                                  <Globe className="w-4 h-4" />
                                  Sites ({devicesData[controller.id].sites.length})
                                </h4>
                                <p className="text-xs text-gray-500">Assign clients and sync devices to assets</p>
                              </div>
                              <div className="grid gap-3">
                                {devicesData[controller.id].sites.map((site) => {
                                  const assignedClient = clients.find(c => c.id === site.assigned_client_id);
                                  return (
                                    <div key={site.id} className="p-4 bg-white rounded-lg border hover:border-blue-200 transition-colors">
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-3">
                                          <div className={`w-3 h-3 rounded-full ${site.status === 'online' ? 'bg-green-500' : 'bg-red-500'}`} />
                                          <div>
                                            <p className="font-medium">{site.name}</p>
                                            <p className="text-xs text-gray-500">
                                              {site.wan_ip && `WAN: ${site.wan_ip}`}
                                              {site.isp && ` • ${site.isp}`}
                                            </p>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <select
                                            value={site.assigned_client_id || ''}
                                            onChange={(e) => handleAssignClientToSite(controller.id, site.id, e.target.value)}
                                            className="text-xs border rounded px-2 py-1 bg-white min-w-[140px]"
                                          >
                                            <option value="">No client assigned</option>
                                            {clients.map(client => (
                                              <option key={client.id} value={client.id}>{client.company_name}</option>
                                            ))}
                                          </select>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="gap-1"
                                            onClick={() => handleSyncSite(controller.id, site.id, site.name)}
                                            disabled={syncingId === `${controller.id}-${site.id}`}
                                          >
                                            {syncingId === `${controller.id}-${site.id}` ? (
                                              <RefreshCw className="w-3 h-3 animate-spin" />
                                            ) : (
                                              <RefreshCw className="w-3 h-3" />
                                            )}
                                            Sync
                                          </Button>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                                        {assignedClient && (
                                          <Badge className="text-xs gap-1 bg-blue-100 text-blue-800">
                                            <Building2 className="w-3 h-3" />
                                            {assignedClient.company_name}
                                          </Badge>
                                        )}
                                        <Badge variant="secondary" className="text-xs gap-1">
                                          <Router className="w-3 h-3" />
                                          {site.device_count || 0} devices
                                        </Badge>
                                        <Badge variant="secondary" className="text-xs gap-1">
                                          <Monitor className="w-3 h-3" />
                                          {site.client_count || 0} clients
                                        </Badge>
                                        <Badge variant={site.status === 'online' ? 'default' : 'destructive'} className="text-xs">
                                          {site.status}
                                        </Badge>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          
                          {/* Hosts (Hardware Consoles) */}
                          {devicesData[controller.id].hosts?.length > 0 && (
                            <div>
                              <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                                <Router className="w-4 h-4" />
                                Dream Machines / Consoles ({devicesData[controller.id].hosts.length})
                              </h4>
                              <div className="grid gap-2 md:grid-cols-2">
                                {devicesData[controller.id].hosts.map((host) => (
                                  <div key={host.id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                                    <div className="flex items-center gap-3">
                                      <div className={`w-2 h-2 rounded-full ${host.status === 'online' ? 'bg-green-500' : 'bg-red-500'}`} />
                                      <div>
                                        <p className="font-medium text-sm">{host.name}</p>
                                        <p className="text-xs text-gray-500">
                                          {host.model && `${host.model}`}
                                          {host.firmware && ` • v${host.firmware}`}
                                          {host.ip && ` • ${host.ip}`}
                                        </p>
                                      </div>
                                    </div>
                                    <Badge variant={host.status === 'online' ? 'default' : 'destructive'} className="text-xs">
                                      {host.status}
                                    </Badge>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Summary */}
                          <div className="text-xs text-gray-500 pt-2 border-t">
                            Total: {devicesData[controller.id].total_sites || 0} sites, {devicesData[controller.id].total_hosts || 0} hosts, {devicesData[controller.id].total_devices || 0} devices
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-500 text-sm text-center py-4">No device data available</p>
                      )}
                    </div>
                  )}

                  {/* Assigned Clients */}
                  <div className="mb-4">
                    <Label className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-2">
                      <Building2 className="w-4 h-4" />
                      Assigned Clients
                    </Label>
                    {controller.clients && controller.clients.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {controller.clients.map((client) => (
                          <Badge key={client.id} variant="secondary" className="gap-1">
                            <Building2 className="w-3 h-3" />
                            {client.company_name}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">No clients assigned</p>
                    )}
                  </div>

                  {/* Last Sync Info */}
                  {controller.last_sync && (
                    <div className="mb-4 text-sm text-gray-500 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Last synced: {new Date(controller.last_sync).toLocaleString()}
                      {controller.last_sync_status === 'success' ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : controller.last_sync_status === 'partial' ? (
                        <XCircle className="w-4 h-4 text-yellow-500" />
                      ) : null}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-2 border-t">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleTestController(controller.id)}
                      disabled={testingId === controller.id}
                    >
                      {testingId === controller.id ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <TestTube className="w-4 h-4 mr-2" />
                      )}
                      Test
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSyncController(controller.id)}
                      disabled={syncingId === controller.id}
                    >
                      {syncingId === controller.id ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Sync All Sites
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingController({ ...controller })}
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-auto"
                      onClick={() => handleDeleteController(controller.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Add Site Manager Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wifi className="w-5 h-5 text-blue-600" />
                Add UniFi Site Manager
              </DialogTitle>
              <DialogDescription>
                Connect to UniFi Cloud to sync network devices and clients to assets
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Controller Name *</Label>
                <Input
                  placeholder="e.g., Main Office, Client Site A"
                  value={newController.name}
                  onChange={(e) => setNewController({ ...newController, name: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Controller URL *</Label>
                <Input
                  placeholder="https://192.168.1.1 or https://unifi.ui.com"
                  value={newController.url}
                  onChange={(e) => setNewController({ ...newController, url: e.target.value })}
                />
                <p className="text-xs text-gray-500">
                  {isCloudUrl(newController.url) 
                    ? '☁️ UniFi Cloud detected - API key required' 
                    : 'Local controller - username/password required'}
                </p>
              </div>
              
              {isCloudUrl(newController.url) ? (
                <div className="space-y-2">
                  <Label>API Key *</Label>
                  <Input
                    type="password"
                    placeholder="Enter your UniFi Site Manager API key"
                    value={newController.api_key}
                    onChange={(e) => setNewController({ ...newController, api_key: e.target.value })}
                  />
                  <p className="text-xs text-gray-500">
                    Get from <a href="https://unifi.ui.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">UniFi Site Manager</a> → Settings → API
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Username *</Label>
                    <Input
                      placeholder="UniFi local admin username"
                      value={newController.username}
                      onChange={(e) => setNewController({ ...newController, username: e.target.value })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Password *</Label>
                    <Input
                      type="password"
                      placeholder="UniFi local admin password"
                      value={newController.password}
                      onChange={(e) => setNewController({ ...newController, password: e.target.value })}
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label>Assign to Clients</Label>
                <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                  {clients.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-2">No clients available</p>
                  ) : (
                    clients.map((client) => (
                      <label key={client.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newController.client_ids.includes(client.id)}
                          onChange={(e) => handleClientSelection(client.id, e.target.checked)}
                          className="rounded"
                        />
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">{client.company_name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <Label>Auto-Sync Devices</Label>
                  <p className="text-xs text-gray-500">Automatically sync devices daily</p>
                </div>
                <Switch
                  checked={newController.auto_sync}
                  onCheckedChange={(checked) => setNewController({ ...newController, auto_sync: checked })}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddController}>
                Add Site Manager
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Site Manager Dialog */}
        <Dialog open={!!editingController} onOpenChange={() => setEditingController(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-blue-600" />
                Edit Site Manager
              </DialogTitle>
            </DialogHeader>
            
            {editingController && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Controller Name</Label>
                  <Input
                    value={editingController.name || ''}
                    onChange={(e) => setEditingController({ ...editingController, name: e.target.value })}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Controller URL</Label>
                  <Input
                    value={editingController.url || ''}
                    onChange={(e) => setEditingController({ ...editingController, url: e.target.value })}
                  />
                  <p className="text-xs text-gray-500">
                    {isCloudUrl(editingController.url) 
                      ? '☁️ UniFi Cloud - API key required' 
                      : 'Local controller - username/password required'}
                  </p>
                </div>
                
                {isCloudUrl(editingController.url) ? (
                  <div className="space-y-2">
                    <Label>API Key</Label>
                    <Input
                      type="password"
                      placeholder="Enter API key (leave blank to keep existing)"
                      value={editingController.api_key || ''}
                      onChange={(e) => setEditingController({ ...editingController, api_key: e.target.value })}
                    />
                    <p className="text-xs text-gray-500">
                      Get from <a href="https://unifi.ui.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">UniFi Site Manager</a> → Settings → API
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>Username</Label>
                      <Input
                        placeholder="UniFi local admin username"
                        value={editingController.username || ''}
                        onChange={(e) => setEditingController({ ...editingController, username: e.target.value })}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Password</Label>
                      <Input
                        type="password"
                        placeholder="Enter new password (leave blank to keep existing)"
                        value={editingController.password || ''}
                        onChange={(e) => setEditingController({ ...editingController, password: e.target.value })}
                      />
                    </div>
                  </>
                )}

                <div className="space-y-2">
                  <Label>Assign to Clients</Label>
                  <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                    {clients.map((client) => (
                      <label key={client.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(editingController.client_ids || []).includes(client.id)}
                          onChange={(e) => handleClientSelection(client.id, e.target.checked, true)}
                          className="rounded"
                        />
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">{client.company_name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <Label>Auto-Sync Devices</Label>
                    <p className="text-xs text-gray-500">Automatically sync devices daily</p>
                  </div>
                  <Switch
                    checked={editingController.auto_sync || false}
                    onCheckedChange={(checked) => setEditingController({ ...editingController, auto_sync: checked })}
                  />
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingController(null)}>
                Cancel
              </Button>
              <Button onClick={() => handleUpdateController(editingController.id)}>
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default UniFiControllers;
